import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Container,
  Row,
  Col,
  Dropdown,
  Form,
  Button,
  Modal,
  Alert,
  Spinner,
} from 'react-bootstrap';
import { PageBusyLoader } from '../ui/PageBusyLoader';
import './Create-Prompt.css';
import { ReactComponent as DeleteIcon } from '../../images/trash3-fill.svg';
import { ReactComponent as PlayIcon } from '../../images/play-fill.svg';
import { ReactComponent as PlusIcon } from '../../images/plus.svg';
import { ReactComponent as DescriptionIcon } from '../../images/portfolio.svg';
import { ReactComponent as UploadIcon } from '../../images/download.svg';
import { ArrowLeft, CheckCircleFill, PencilSquare } from 'react-bootstrap-icons';
import { ReactComponent as EditIcon } from '../../images/edit.svg';
import { ReactComponent as CaseIcon } from '../../images/case.svg';
import thumbsUpEncouragement from '../../images/thumbs-up-encouragement.png';
import { parseRankedAiPromptList, rankedPromptTexts } from '../../utils/parseRankedAiPromptList';
import { industries } from '../../config/industries';
import { generalPrompts } from '../../config/generalPrompts';
import { useNavigate } from 'react-router-dom';
import { BASE_URL, API_ROUTES } from '../../config/api-routes';
import { validationMessages } from '../../config/validationMessages';
import PromptSelectionModal from '../PromptSelectionModal';
import RecordOrUploadModal from '../RecordOrUploadModal';
import PromptDescriptionModal from '../PromptDescriptionModal';
import { useSessionStore } from '../../store/sessionStore';
import { toast } from 'react-toastify';
import CongratsModal from '../CongratsModal';
import PromptManageModal from './PromptManageModal';
import PromptSummaryResponseSummary from './PromptSummaryResponseSummary';
import OnboardingProgress from '../OnboardingProgress';
import RecommendationModal from '../RecommendationModal';
import axiosInstance, { handleUnauthorized } from '../../api/api';
import { isAccessTokenExpired } from '../../utils/authSession';
import { loadCachedRolePrompts, saveCachedRolePrompts } from '../../utils/rolePromptCache';
import {
  computeProfileCompletionScore,
  draftPromptsForScoring,
  profileCompletionMessage,
  resolveScreeningReadinessPercent,
} from '../../utils/profileCompletionScore';
import {
  clearPendingRoleChange,
  isPendingRoleChange,
  isPromptSetupComplete,
  markPendingRoleChange,
  markPromptSetupComplete,
  normalizePromptListResponse,
  persistCurrentUserPatch,
} from '../../utils/userProfile';
import { getUserFacingError } from '../../utils/userFacingError';
import { PromptVideoUploadBar } from '../Prompts/PromptVideoUploadBar';
import { useCreatePromptDocumentUploads } from './useCreatePromptDocumentUploads';
import { useCreatePromptVideoUploads } from './useCreatePromptVideoUploads';

type LevelKeys = 'entry' | 'mid' | 'senior' | 'none' | '';

const JOB_LEVEL_OPTIONS: Exclude<LevelKeys, ''>[] = ['none', 'entry', 'mid', 'senior'];

interface IErrors {
  noIndustry: boolean;
  noTitle: boolean;
  noLevel: boolean;
  videoFile: string;
}

type VideoFiles = {
  [key: number]: File;
};

const { PROVIDE_LEVEL, PROVIDE_JOB_TITLE, PROVIDE_INDUSTRY } = validationMessages;

function getRoleSetupEncouragement(industry: string, title: string, jobLevel: LevelKeys): string {
  if (jobLevel) {
    return "Perfect — we'll tailor your screening prompts to your experience level.";
  }
  if (title.trim()) {
    return 'Great title! Now pick the job level that matches your experience.';
  }
  if (industry.trim()) {
    return 'Nice choice! Add your job title so we can personalize your prompts.';
  }
  return "You're doing awesome! We'll help you shine by showcasing your strengths.";
}

const ROLE_PROMPT_POOL_SIZE = 30;

const GENERIC_PREFILL_COUNT = 2;
const ROLE_PREFILL_COUNT = 2;

/** Two generic + two role-specific (most recommended, then first recommended). */
const buildDefaultPromptSet = (roleSpecificPrompts: string[]): string[] => {
  const generic = generalPrompts.slice(0, GENERIC_PREFILL_COUNT);
  const roleTop = roleSpecificPrompts.slice(0, ROLE_PREFILL_COUNT);
  return [...generic, ...roleTop];
};

const PROFILE_STORAGE_CONSENT_TEXT =
  'By creating a profile, you consent to SkipScreen storing and sharing your profile information, uploaded content, and screening responses with recruiters and employers you authorize.';

const formatJobLevel = (lvl: LevelKeys): string => {
  if (lvl === 'entry') return 'Entry level';
  if (lvl === 'mid') return 'Mid level';
  if (lvl === 'senior') return 'Senior level';
  if (lvl === 'none') return 'Not specified';
  return '';
};

function countWords(text: string): number {
  const trimmed = text.trim();
  if (!trimmed) {
    return 0;
  }
  return trimmed.split(/\s+/).filter(Boolean).length;
}

function promptRowStatus(hasVideo: boolean, hasDescription: boolean): 'complete' | 'partial' {
  return hasVideo && hasDescription ? 'complete' : 'partial';
}

function readSavedTargetRole(user: Record<string, unknown> | null | undefined) {
  if (!user) {
    return { industry: '', title: '', level: '' as LevelKeys };
  }
  return {
    industry: String(user.jobIndustry ?? user.job_industry ?? '').trim(),
    title: String(user.jobTitle ?? user.job_title ?? '').trim(),
    level: String(user.promptLevel ?? user.prompt_level ?? '').trim() as LevelKeys,
  };
}

const CreatePrompt: React.FC = () => {
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [jobIndustry, setJobIndustry] = useState<string>('');
  const [jobTitle, setJobTitle] = useState<string>('');
  const [level, setLevel] = useState<LevelKeys>('');
  const [showModal, setShowModal] = useState<boolean>(false);
  const [, setModalStep] = useState<number>(1);
  const [recordOrUploadModal, setRecordOrUploadModal] = useState<boolean>(false);
  const [promptArray, setPromptArray] = useState<string[]>([]);
  const [promptIds, setPromptIds] = useState<Record<number, number>>({});
  const [deletingPromptIndex, setDeletingPromptIndex] = useState<number | null>(null);
  const [roleSpecificPromptPool, setRoleSpecificPromptPool] = useState<string[]>([]);

  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [loader, setLoader] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [showPortfolioModal, __] = useState<boolean>(false);
  const [errors, setErrors] = useState<IErrors>({
    noIndustry: false,
    noTitle: false,
    noLevel: false,
    videoFile: '',
  });
  const [openPromptIndex, setOpenPromptIndex] = useState<number | null>(null);
  const [videoFiles, setVideoFiles] = useState<VideoFiles>({});
  const [showVideoPlayer, setShowVideoPlayer] = useState(false);
  const [selectedVideoUrl, setSelectedVideoUrl] = useState<string | null>(null);
  const [documentFiles, setDocumentFiles] = useState<{ [key: number]: File }>({});
  const [descriptions, setDescriptions] = useState<{ [key: number]: string }>({});
  const [isDescriptionModalOpen, setIsDescriptionModalOpen] = useState(false);
  const [descriptionModalMode, setDescriptionModalMode] = useState<'view' | 'edit'>('edit');
  const [currentDescription, setCurrentDescription] = useState('');
  const promptsHydratedRef = useRef(false);
  const [showFinalizeModal, setShowFinalizeModal] = useState(false);
  const [profileConsentAccepted, setProfileConsentAccepted] = useState(false);
  const [showWholePromptRemovalModal, setWholePromptRemovalModal] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, setPortfolioData] = useState<any>(null);
  const editingIndexRef = useRef<number | null>(null);
  // Track if a video was added in the current modal session
  const previousVideoCountRef = useRef<number>(0);
  const [showCongrats, setShowCongrats] = useState(false);
  const [showRecommendation, setShowRecommendation] = useState(false);
  const maxPrompts = 6;
  const minPrompts = 4;

  const navigate = useNavigate();

  // track previous video count so we can detect 0 -> 1 transition
  //const prevVideoCountRef = useRef<number>(Object.keys(videoFiles).length);
  //const congratsFiredRef = useRef<boolean>(false); // prevent duplicate modal in same session
  const [showInfoPopup, setShowInfoPopup] = useState(false);
  const changeRoleFlow = isPendingRoleChange();
  const [showFirstPromptMilestone, setShowFirstPromptMilestone] = useState(false);
  const [dismissedEnhancementCards, setDismissedEnhancementCards] = useState<{
    [key: number]: boolean;
  }>({});

  const {
    videoUrls,
    videoUploads,
    clearVideoUploadState,
    clearAllVideoUploadState,
    reindexVideoUploadState,
    retryVideoUpload,
    cancelVideoUpload,
    hasPendingVideoUploads,
    hasVideoUploadErrors,
    seedVideoUrls,
    replaceVideoUrls,
  } = useCreatePromptVideoUploads(videoFiles);

  const {
    documentUrls,
    clearDocumentUploadState,
    reindexDocumentUploadState,
    hasPendingDocumentUploads,
    hasDocumentUploadErrors,
    seedDocumentUrls,
    replaceDocumentUrls,
  } = useCreatePromptDocumentUploads(documentFiles);

  const hasPromptDocument = (index: number) => Boolean(documentFiles[index] || documentUrls[index]);

  const videoCount = promptArray.filter((_, index) =>
    Boolean(videoFiles[index] || videoUrls[index])
  ).length;
  const remainingVideos = Math.max(4 - videoCount, 0);

  useEffect(() => {
    if (currentStep !== 3) {
      return;
    }
    window.scrollTo({ top: 0, behavior: 'auto' });
    document.querySelector('.main-content')?.scrollTo({ top: 0, behavior: 'auto' });
  }, [currentStep]);

  useEffect(() => {
    localStorage.setItem('createPromptStep', String(currentStep));

    const draftPrompts = draftPromptsForScoring(
      promptArray.map((_, index) => ({
        hasVideo: Boolean(videoFiles[index] || videoUrls[index]),
        hasDescription: Boolean(descriptions[index]?.trim()),
      }))
    );

    const session = useSessionStore.getState();
    const result = computeProfileCompletionScore(
      session.currentUser,
      draftPrompts,
      session.portfolio
    );

    window.dispatchEvent(
      new CustomEvent('prompt-progress', {
        detail: {
          progress: result.percent,
          message: profileCompletionMessage(result),
          videoCount: Object.keys(videoFiles || {}).length,
        },
      })
    );
    sessionStorage.setItem(
      'latestPromptProgress',
      JSON.stringify({
        progress: result.percent,
        videoCount: Object.keys(videoFiles || {}).length,
      })
    );
  }, [currentStep, promptArray, videoFiles, videoUrls, documentFiles, descriptions]);

  /*   useEffect(() => {
    const prev = prevVideoCountRef.current;
    const current = Object.keys(videoFiles).length;

    // detect first video added this session AND must be on step 2 (Customize a Prompt)
    if (
      !congratsFiredRef.current &&
      prev === 0 &&
      current >= 1 &&
      currentStep === 2
    ) {
      congratsFiredRef.current = true; // ensure it only fires once per session
      //setShowCongrats(true); // show your Congrats modal immediately

      // optional: notify the backend so it can mark firstUpload (persistent)
      (async () => {
        try {
          // const accessToken = localStorage.getItem('accessToken');
          // // replace endpoint with your real video upload/check endpoint
          // const res = await fetch(`${BASE_URL}prompts/mark-first-upload`, {
          //   method: 'POST',
          //   headers: {
          //     'Content-Type': 'application/json',
          //     authorization: `Bearer ${accessToken}`,
          //   },
          //   body: JSON.stringify({ firstUploadAt: new Date().toISOString() }),
          // });
          // // backend could return { firstUpload: true } if it was the user's first ever
          // const json = await res.json();
          // you can use json.firstUpload for analytics or alternate messaging
        } catch (err) {
          // ignore silently — UI already shown
          console.warn('failed to notify backend about first upload', err);
        }
      })();
    }

    // update prev count for next run
    prevVideoCountRef.current = current;
  }, [videoFiles, currentStep]); */

  useEffect(() => {
    if (jobTitle !== '') {
      setErrors((prev) => ({ ...prev, noTitle: false }));
    }
  }, [jobTitle]);

  useEffect(() => {
    if (!jobTitle || !jobIndustry || !level) {
      return;
    }

    const cached = loadCachedRolePrompts(jobTitle, jobIndustry, level);
    if (cached.length > 0) {
      setRoleSpecificPromptPool(rankedPromptTexts(cached));
    }
  }, [jobTitle, jobIndustry, level]);

  useEffect(() => {
    const cached = useSessionStore.getState().portfolio;
    if (cached) {
      setPortfolioData(cached);
      return;
    }
    if (!localStorage.getItem('accessToken')) return;
    void useSessionStore
      .getState()
      .bootstrapSession()
      .then(({ portfolio }) => {
        if (portfolio) setPortfolioData(portfolio);
      })
      .catch((error: unknown) => {
        const status = (error as { response?: { status?: number } })?.response?.status;
        if (status === 401 || status === 403) {
          handleUnauthorized();
        }
      });
  }, []);

  useEffect(() => {
    if (promptsHydratedRef.current) {
      return;
    }

    setLoader(true);
    const getPrompts = async () => {
      const accessToken = localStorage.getItem('accessToken');

      const response = await fetch(`${BASE_URL}prompts`, {
        method: 'GET',
        headers: {
          authorization: `Bearer ${accessToken}`,
        },
      });

      if (response.status === 401 || response.status === 403) {
        handleUnauthorized();
        return;
      }

      if (!response.ok) {
        setLoader(false);
        return;
      }

      const data = await response.json();
      const promptList = normalizePromptListResponse(data);
      const session = await useSessionStore.getState().bootstrapSession({
        force: true,
      });
      const user = session.user as Record<string, unknown> | null;
      const changeRole = isPendingRoleChange();
      if (isPromptSetupComplete(user) && !changeRole) {
        navigate('/dashboard', { replace: true });
        setLoader(false);
        return;
      }

      if (changeRole) {
        markPendingRoleChange();
        const savedRole = readSavedTargetRole(user);
        if (savedRole.industry) setJobIndustry(savedRole.industry);
        if (savedRole.title) setJobTitle(savedRole.title);
        if (savedRole.level) setLevel(savedRole.level);
        setCurrentStep(1);
        setPromptArray([]);
        promptsHydratedRef.current = true;
        setLoader(false);
        return;
      }

      const savedRole = readSavedTargetRole(user);
      if (savedRole.industry) setJobIndustry(savedRole.industry);
      if (savedRole.title) setJobTitle(savedRole.title);
      if (savedRole.level) setLevel(savedRole.level);

      if (promptList.length > 0) {
        useSessionStore.getState().setPrompts(promptList);

        const rows = promptList as Array<Record<string, unknown>>;
        const contents = rows.map((row) => String(row.content ?? '').trim()).filter(Boolean);

        if (contents.length > 0) {
          const first = rows[0];
          const savedIndustry = String(first.jobIndustry ?? first.job_industry ?? '').trim();
          const savedTitle = String(first.jobTitle ?? first.job_title ?? '').trim();
          const savedLevel = String(first.level ?? '').trim() as LevelKeys;

          if (savedIndustry) setJobIndustry(savedIndustry);
          if (savedTitle) setJobTitle(savedTitle);
          if (savedLevel) setLevel(savedLevel);
          setPromptArray(contents);

          const nextDescriptions: Record<number, string> = {};
          const nextVideoUrls: Record<number, string> = {};
          const nextDocumentUrls: Record<number, string> = {};
          const nextPromptIds: Record<number, number> = {};
          rows.forEach((row, index) => {
            const description = String(row.description ?? '').trim();
            if (description) {
              nextDescriptions[index] = description;
            }
            const fileUrl = String(row.fileUrl ?? row.file_url ?? '').trim();
            if (fileUrl && fileUrl !== 'N/A') {
              nextVideoUrls[index] = fileUrl;
            }
            const docUrl = String(
              row.supportedDocumentUrl ?? row.supported_document_url ?? ''
            ).trim();
            if (docUrl) {
              nextDocumentUrls[index] = docUrl;
            }
            const promptId = Number(row.id);
            if (Number.isFinite(promptId) && promptId > 0) {
              nextPromptIds[index] = promptId;
            }
          });
          setDescriptions(nextDescriptions);
          setPromptIds(nextPromptIds);
          seedVideoUrls(nextVideoUrls);
          seedDocumentUrls(nextDocumentUrls);

          const savedStep = parseInt(localStorage.getItem('createPromptStep') ?? '2', 10);
          if (contents.length >= minPrompts) {
            setCurrentStep(3);
          } else if (contents.length > 0 || savedStep >= 2) {
            setCurrentStep(2);
          }
        }
      }

      promptsHydratedRef.current = true;
      setLoader(false);
    };
    getPrompts();
  }, [navigate, seedVideoUrls, seedDocumentUrls, minPrompts]);

  const handleOpenDescriptionModal = (index: number) => {
    setCurrentDescription(descriptions[index] || '');
    setDescriptionModalMode('edit');
    setIsDescriptionModalOpen(true);
    setEditingIndex(index);
    editingIndexRef.current = index;
  };

  const handleViewDescription = (index: number) => {
    setCurrentDescription(descriptions[index] || '');
    setDescriptionModalMode('view');
    setIsDescriptionModalOpen(true);
    setEditingIndex(index);
    editingIndexRef.current = index;
  };

  const handleCancelDescription = () => {
    setIsDescriptionModalOpen(false);
    setDescriptionModalMode('edit');
    setEditingIndex(null);
    editingIndexRef.current = null;
  };

  const togglePrompt = (index: number) => {
    if (openPromptIndex === index) {
      setOpenPromptIndex(null);
    } else {
      setOpenPromptIndex(index);
    }
  };

  /* const handleSave = () => {
    // Show the incomplete portfolio alert
    setShowSaveAlert(true);
  }; */
  const buildPromptsFormData = (): FormData => {
    const formData = new FormData();
    const promptsData = promptArray.map((prompt, index) => ({
      content: prompt,
      description: descriptions[index] || '',
      jobTitle,
      jobIndustry,
      level,
      ...(videoUrls[index]
        ? { fileUrl: videoUrls[index] }
        : videoFiles[index]
          ? { fileId: `file-${index}` }
          : {}),
      ...(documentUrls[index]
        ? { supportedDocumentUrl: documentUrls[index] }
        : documentFiles[index]
          ? { supportDocumentFileId: `doc-${index}` }
          : {}),
    }));

    formData.append('prompts', JSON.stringify(promptsData));

    Object.keys(videoFiles).forEach((key) => {
      const numericKey = parseInt(key, 10);
      if (videoUrls[numericKey]) return;
      const file = videoFiles[numericKey];
      if (file && file instanceof File && file.name) {
        formData.append(`file-${numericKey}`, file, file.name);
      }
    });

    Object.keys(documentFiles).forEach((keyString) => {
      const numericKey = parseInt(keyString, 10);
      if (documentUrls[numericKey]) return;
      const file = documentFiles[numericKey];
      if (file && file instanceof File && file.name) {
        formData.append(`doc-${numericKey}`, file, file.name);
      }
    });

    return formData;
  };

  const syncPromptIdsFromResponse = (saved: unknown[]) => {
    const nextIds: Record<number, number> = {};
    saved.forEach((row, index) => {
      const id = Number((row as Record<string, unknown>).id);
      if (Number.isFinite(id) && id > 0) {
        nextIds[index] = id;
      }
    });
    setPromptIds(nextIds);
  };

  const syncMediaFromSavedPrompts = (saved: unknown[]) => {
    const rows = saved as Array<Record<string, unknown>>;
    const nextVideoUrls: Record<number, string> = {};
    const nextDocumentUrls: Record<number, string> = {};
    rows.forEach((row, index) => {
      const fileUrl = String(row.fileUrl ?? row.file_url ?? '').trim();
      if (fileUrl && fileUrl !== 'N/A') {
        nextVideoUrls[index] = fileUrl;
      }
      const docUrl = String(row.supportedDocumentUrl ?? row.supported_document_url ?? '').trim();
      if (docUrl) {
        nextDocumentUrls[index] = docUrl;
      }
    });
    replaceVideoUrls(nextVideoUrls);
    replaceDocumentUrls(nextDocumentUrls);
    setVideoFiles({});
    setDocumentFiles({});
  };

  const saveAllPrompts = async (): Promise<unknown[]> => {
    const accessToken = localStorage.getItem('accessToken');
    const response = await fetch(`${BASE_URL}prompt`, {
      method: 'POST',
      headers: {
        authorization: `Bearer ${accessToken}`,
      },
      body: buildPromptsFormData(),
    });

    if (!response.ok) {
      let err: unknown = null;
      try {
        err = await response.json();
      } catch {
        err = { error: response.statusText || 'Request failed' };
      }
      throw err;
    }

    const saved = await response.json();
    return normalizePromptListResponse(saved);
  };

  const handleSave = async () => {
    if (promptArray.length < 4) {
      toast.error('You need at least 4 prompts');
      return;
    }

    if (hasPendingVideoUploads()) {
      toast.error('Please wait for video uploads to finish.');
      return;
    }

    if (hasVideoUploadErrors()) {
      toast.error('Fix failed video uploads before saving.');
      return;
    }

    if (hasPendingDocumentUploads()) {
      toast.error('Please wait for document uploads to finish.');
      return;
    }

    if (hasDocumentUploadErrors()) {
      toast.error('Fix failed document uploads before saving.');
      return;
    }

    setIsSaving(true);

    try {
      const savedPrompts = await saveAllPrompts();
      syncPromptIdsFromResponse(savedPrompts);
      syncMediaFromSavedPrompts(savedPrompts);
      useSessionStore.getState().setPrompts(savedPrompts);
      await useSessionStore.getState().bootstrapSession({ force: true });
      await useSessionStore.getState().refreshPrompts({ force: true });
      window.dispatchEvent(new Event('prompts-updated'));

      toast.success(
        currentStep === 3
          ? 'Prompts saved. You can continue later or submit when ready.'
          : 'Your prompts have been saved. Keep editing here, or click Next to review your summary and submit.',
        { autoClose: 5000 }
      );
    } catch (error) {
      console.error('Save error:', error);
      toast.error(getUserFacingError(error, 'We could not save your prompts. Please try again.'));
    } finally {
      setIsSaving(false);
    }
  };

  const onModalClose = () => {
    setShowModal(false);
    setOpenPromptIndex(null);
    setEditingIndex(null);
    editingIndexRef.current = null;
  };

  const onModalOpen = (index?: number) => {
    setModalStep(1);
    setOpenPromptIndex(null);

    if (typeof index === 'number') {
      setEditingIndex(index); // editing mode
      editingIndexRef.current = index;
    } else {
      setEditingIndex(null);
      editingIndexRef.current = null;
    }

    setShowModal(true);
  };

  const closeFinalizeModal = () => {
    setShowFinalizeModal(false);
    setProfileConsentAccepted(false);
  };

  const onSubmit = () => {
    if (isSubmitting) {
      return;
    }
    if (promptArray.length < 4) {
      toast.error('You need at least 4 prompts before submitting.');
      return;
    }
    if (hasPendingVideoUploads()) {
      toast.error('Please wait for video uploads to finish.');
      return;
    }
    if (hasVideoUploadErrors()) {
      toast.error('Fix failed video uploads before submitting.');
      return;
    }
    if (hasPendingDocumentUploads()) {
      toast.error('Please wait for document uploads to finish.');
      return;
    }
    if (hasDocumentUploadErrors()) {
      toast.error('Fix failed document uploads before submitting.');
      return;
    }
    setProfileConsentAccepted(false);
    setShowFinalizeModal(true);
  };

  const handleConfirmFinalize = async () => {
    if (!profileConsentAccepted) {
      toast.error('Please accept the consent statement to continue.');
      return;
    }
    setShowFinalizeModal(false);
    setProfileConsentAccepted(false);
    setIsSubmitting(true);

    try {
      if (promptArray.length < 4) {
        toast.error('You need at least 4 prompts before submitting.');
        return;
      }

      if (hasPendingVideoUploads()) {
        toast.error('Please wait for video uploads to finish.');
        return;
      }

      if (hasVideoUploadErrors()) {
        toast.error('Fix failed video uploads before submitting.');
        return;
      }

      if (hasPendingDocumentUploads()) {
        toast.error('Please wait for document uploads to finish.');
        return;
      }

      if (hasDocumentUploadErrors()) {
        toast.error('Fix failed document uploads before submitting.');
        return;
      }

      const savedPrompts = await saveAllPrompts();
      syncPromptIdsFromResponse(savedPrompts);
      syncMediaFromSavedPrompts(savedPrompts);
      useSessionStore.getState().setPrompts(savedPrompts);
      await useSessionStore.getState().bootstrapSession({ force: true });
      await useSessionStore.getState().refreshPrompts({ force: true });
      window.dispatchEvent(new Event('prompts-updated'));

      const user = useSessionStore.getState().currentUser as Record<string, unknown> | null;
      markPromptSetupComplete(user?.id as string | number | undefined);
      persistCurrentUserPatch({
        onboardingCompleted: true,
        onboarding_completed: true,
      });

      try {
        await axiosInstance.post(API_ROUTES.AUTH.COMPLETE_PROFILE, {
          onboardingCompleted: true,
        });
      } catch (profileErr) {
        console.error('Failed to persist onboarding completion:', profileErr);
      }

      toast.success('Your screening prompts have been submitted!');
      navigate('/dashboard', { replace: true });
    } catch (error) {
      console.error('❌ Network error:', error);
      toast.error(getUserFacingError(error, 'Something went wrong while saving prompts.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleModalClose = () => {
    setRecordOrUploadModal(false);
    const indexForManage = editingIndexRef.current ?? editingIndex ?? openPromptIndex;
    if (indexForManage !== null && indexForManage !== undefined) {
      setOpenPromptIndex(indexForManage);
    }
  };

  const celebratePromptSaveWithVideo = async () => {
    if (currentStep !== 2) {
      return;
    }

    const savedVideoCount = promptArray.filter((_, index) =>
      Boolean(videoFiles[index] || videoUrls[index])
    ).length;

    if (savedVideoCount < 1 || savedVideoCount >= 4) {
      return;
    }

    try {
      const user = await useSessionStore.getState().refreshUser({ force: true });

      if ((user as { firstPromptCongrats?: boolean })?.firstPromptCongrats === false) {
        setShowCongrats(true);
        axiosInstance
          .post(API_ROUTES.USER.MARK_FIRST_PROMPT)
          .catch((err) => console.error('Mark first prompt failed:', err));
        return;
      }

      setShowFirstPromptMilestone(true);
    } catch (err) {
      console.error('Error fetching user for congrats flag:', err);
      setShowFirstPromptMilestone(true);
    }
  };

  const handleFirstPromptCongratsClose = () => {
    setShowCongrats(false);

    if (currentStep === 2 && videoCount >= 1 && videoCount < 4) {
      setShowFirstPromptMilestone(true);
    }
  };

  const ensurePromptInSet = (prompt: string): number | null => {
    const existing = promptArray.findIndex((p) => p === prompt);
    if (existing >= 0) {
      return existing;
    }
    if (promptArray.length >= maxPrompts) {
      toast.error(`You can only have ${maxPrompts} prompts max.`);
      return null;
    }
    const newIndex = promptArray.length;
    setPromptArray((prev) => [...prev, prompt]);
    return newIndex;
  };

  const handlePromptSelect = (selectedPrompt: string, options?: { openVideo?: boolean }) => {
    const openVideo = options?.openVideo === true;
    setShowModal(false);
    setModalStep(1);

    if (editingIndex !== null && editingIndex >= 0 && editingIndex < promptArray.length) {
      const idx = editingIndex;
      setPromptArray((prev) => {
        const next = [...prev];
        next[idx] = selectedPrompt;
        return next;
      });
      setOpenPromptIndex(null);

      if (openVideo) {
        previousVideoCountRef.current = Object.keys(videoFiles).length;
        setTimeout(() => setRecordOrUploadModal(true), 100);
      } else {
        setEditingIndex(null);
        toast.success('Prompt updated.');
      }
      return;
    }

    const existingIdx = promptArray.findIndex((p) => p === selectedPrompt);
    if (existingIdx >= 0) {
      toast.info('This prompt is already in your set.');
      setEditingIndex(null);
      return;
    }

    if (promptArray.length >= maxPrompts) {
      toast.error(`You can only have ${maxPrompts} prompts max.`);
      return;
    }

    const newIndex = promptArray.length;
    setPromptArray((prev) => [...prev, selectedPrompt]);
    setEditingIndex(newIndex);
    setOpenPromptIndex(null);

    if (openVideo) {
      previousVideoCountRef.current = Object.keys(videoFiles).length;
      setTimeout(() => setRecordOrUploadModal(true), 100);
    } else {
      setEditingIndex(null);
      toast.success('Prompt added to your set.');
    }
  };

  /** Video, description, and documents for the prompt at this index. */
  const handleCustomizePrompt = (index: number) => {
    setEditingIndex(index);
    editingIndexRef.current = index;
    setShowModal(false);
    setOpenPromptIndex(index);
  };

  const renderPromptManageModal = (index: number) => {
    const item = promptArray[index];
    if (!item) {
      return null;
    }

    const hasVideo = Boolean(videoFiles[index] || videoUrls[index]);
    const hasDescription = Boolean(descriptions[index]?.trim());
    const hasDocument = hasPromptDocument(index);

    return (
      <PromptManageModal
        show
        promptText={item}
        descriptionPreview={descriptions[index]?.trim()}
        hasVideo={hasVideo}
        hasDescription={hasDescription}
        hasDocument={hasDocument}
        uploadProgressSlot={
          isVideoUploadActive(index)
            ? renderVideoUploadBar(
                index,
                'create-prompt-video-upload create-prompt-video-upload--manage'
              )
            : null
        }
        onClose={() => setOpenPromptIndex(null)}
        onRecordOrUpload={() => openRecordFlowForIndex(index)}
        onDeleteVideo={() => {
          setVideoFiles((prev) => {
            const updated = { ...prev };
            delete updated[index];
            return updated;
          });
          clearVideoUploadState(index);
          toast.success('Video deleted successfully!');
        }}
        onViewVideo={() => viewVideo(index)}
        onDescription={() => handleOpenDescriptionModal(index)}
        onUploadDocument={() => document.getElementById(`doc-upload-${index}`)?.click()}
        onViewDocument={() => {
          const file = documentFiles[index];
          if (file) {
            window.open(URL.createObjectURL(file), '_blank');
          }
        }}
        onDeleteDocument={() => {
          setDocumentFiles((prev) => {
            const updated = { ...prev };
            delete updated[index];
            return updated;
          });
          clearDocumentUploadState(index);
          toast.success('Supporting document removed.');
        }}
        onChangePrompt={() => {
          setOpenPromptIndex(null);
          handleBrowsePromptList(index);
        }}
        onDone={async () => {
          setOpenPromptIndex(null);
          toast.success('Prompt response saved.');
          if (hasVideo) {
            await celebratePromptSaveWithVideo();
          }
        }}
      />
    );
  };

  /** Pick a different prompt text from the general / role-specific lists. */
  const handleBrowsePromptList = (index: number) => {
    setEditingIndex(index);
    editingIndexRef.current = index;
    setOpenPromptIndex(null);
    setModalStep(1);
    setShowModal(true);
  };

  const openRecordFlowForIndex = (index: number) => {
    setEditingIndex(index);
    editingIndexRef.current = index;
    setShowModal(false);
    previousVideoCountRef.current = Object.keys(videoFiles).length;
    setRecordOrUploadModal(true);
  };

  const handleSelectIndustry = (eventKey: string | null) => {
    setJobIndustry(eventKey || '');
    setErrors((prev) => ({ ...prev, noIndustry: false }));
  };

  const handleChangeLevel = (level: LevelKeys) => {
    setLevel(level);
    setErrors((prev) => ({ ...prev, noLevel: false }));
  };

  const handleNextStep = async () => {
    if (currentStep === 2) {
      if (promptArray.length < minPrompts) {
        toast.error(`Select at least ${minPrompts} prompts before continuing.`);
        return;
      }
      setCurrentStep(3);
      return;
    }

    if (currentStep !== 1) {
      setCurrentStep((prev) => prev + 1);
      return;
    }

    if (jobIndustry === '' || jobTitle === '' || !level) {
      if (jobIndustry === '') {
        setErrors((prev) => ({ ...prev, noIndustry: true }));
      }

      if (jobTitle === '' && jobIndustry !== '') {
        setErrors((prev) => ({ ...prev, noTitle: true }));
      }

      if (!level && jobTitle !== '') {
        setErrors((prev) => ({ ...prev, noLevel: true }));
      }

      return;
    }

    if (isAccessTokenExpired()) {
      handleUnauthorized();
      return;
    }

    setLoader(true);

    try {
      const roleSave = await axiosInstance.post(API_ROUTES.AUTH.COMPLETE_PROFILE, {
        jobIndustry,
        jobTitle,
        promptLevel: level,
      });
      const savedUser = (roleSave.data as { user?: Record<string, unknown> })?.user;
      if (savedUser) {
        useSessionStore.getState().setCurrentUser(savedUser);
      }
    } catch (roleErr) {
      console.error('Failed to save target role:', roleErr);
      toast.error('Could not save your target role. Please try again.');
      setLoader(false);
      return;
    }

    if (isPendingRoleChange()) {
      try {
        await axiosInstance.delete(API_ROUTES.PROMPTS.DELETE_ALL_PROMPTS);
        useSessionStore.getState().setPrompts([]);
        clearPendingRoleChange();
        toast.info('Your previous screening prompts were removed for your new role.');
      } catch (deleteErr) {
        console.error('Failed to clear prompts for role change:', deleteErr);
        toast.error('Could not clear your previous prompts. Please try again.');
        setLoader(false);
        return;
      }
    }

    const cached = loadCachedRolePrompts(jobTitle, jobIndustry, level);
    if (cached.length > 0) {
      const roleTexts = rankedPromptTexts(cached);
      setRoleSpecificPromptPool(roleTexts);
      setPromptArray(buildDefaultPromptSet(roleTexts));
      setCurrentStep(2);
      setLoader(false);
      return;
    }

    const rolePoolQuestion = `Return exactly ${ROLE_PROMPT_POOL_SIZE} screening interview questions for a ${jobTitle} (${jobIndustry}) at ${level} level.
Order them from most to least effective for screening.
Use this format:
1. [MOST RECOMMENDED] <single best question>
2. [RECOMMENDED] <second best question>
3. [RECOMMENDED] <third best question>
4. [RECOMMENDED] <fourth best question>
5-${ROLE_PROMPT_POOL_SIZE}. <question only, no badge tags>
One question per line, numbered 1-${ROLE_PROMPT_POOL_SIZE}, no group headings.`;

    try {
      const roleResult = await axiosInstance.post(API_ROUTES.AI.ASK_QUESTION, {
        question: rolePoolQuestion,
      });

      const roleData = roleResult.data;

      const rolePrompts = parseRankedAiPromptList(roleData.answer, ROLE_PROMPT_POOL_SIZE);
      const roleTexts = rankedPromptTexts(rolePrompts);
      setRoleSpecificPromptPool(roleTexts);
      saveCachedRolePrompts(jobTitle, jobIndustry, level, rolePrompts);
      setPromptArray(buildDefaultPromptSet(roleTexts));
      setCurrentStep(2);
    } catch (error: unknown) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 401 || status === 403) {
        return;
      }
      console.error('Network error:', error);
      toast.error('Could not generate prompts. Please try again.');
    } finally {
      setLoader(false);
    }
  };

  const handlePrevStep = () => {
    if (currentStep > 1) {
      if (currentStep === 2) {
        setPromptArray([]);
        setVideoFiles({});
        clearAllVideoUploadState();
        setDescriptions({});
        setDocumentFiles({});
        setOpenPromptIndex(null);
        setEditingIndex(null);
      }
      setCurrentStep(currentStep - 1);
    }
  };

  const handleDeleteWholePrompt = () => {
    setPromptArray([]);
    setVideoFiles({});
    clearAllVideoUploadState();
    setDescriptions({});
    setDocumentFiles({});
    setCurrentStep(1);
    setWholePromptRemovalModal(false);
  };

  const handleLevelExperience = (level: LevelKeys) => {
    switch (level) {
      case 'entry':
        return 'Entry Level (0-2 years)';
      case 'mid':
        return 'Mid Level (3-7 years)';
      case 'senior':
        return 'Senior Level (8+ years)';
      case 'none':
        return 'No Experience';
      default:
        return '';
    }
  };

  const navigateToUserProfile = () => {
    navigate('/user-profile');
  };

  const viewVideo = (index: number) => {
    const file = videoFiles[index];
    const savedUrl = videoUrls[index];
    if (file) {
      setSelectedVideoUrl(URL.createObjectURL(file));
      setShowVideoPlayer(true);
      return;
    }
    if (savedUrl) {
      setSelectedVideoUrl(savedUrl);
      setShowVideoPlayer(true);
    }
  };

  const viewDocument = (index: number) => {
    const file = documentFiles[index];
    const savedUrl = documentUrls[index];
    if (file) {
      window.open(URL.createObjectURL(file), '_blank', 'noopener,noreferrer');
      return;
    }
    if (savedUrl) {
      window.open(savedUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const renderVideoUploadBar = (index: number, className = 'create-prompt-video-upload') => {
    const upload = videoUploads[index];
    if (!upload || upload.phase === 'done') {
      return null;
    }

    return (
      <div className={className}>
        <PromptVideoUploadBar progress={upload.percent} phase={upload.phase} compact />
        <div className="create-prompt-video-upload-actions">
          {upload.phase === 'error' ? (
            <button
              type="button"
              className="create-prompt-video-upload-retry"
              onClick={() => retryVideoUpload(index)}
            >
              Retry upload
            </button>
          ) : null}
          {upload.phase !== 'error' ? (
            <button
              type="button"
              className="create-prompt-video-upload-cancel"
              onClick={() => cancelVideoUpload(index)}
            >
              Cancel upload
            </button>
          ) : null}
        </div>
      </div>
    );
  };

  const isVideoUploadActive = (index: number) => {
    const upload = videoUploads[index];
    return Boolean(upload && upload.phase !== 'done');
  };

  const uploadsPending = hasPendingVideoUploads() || hasPendingDocumentUploads();

  const closeVideoPlayer = () => {
    if (selectedVideoUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(selectedVideoUrl);
    }
    setShowVideoPlayer(false);
    setSelectedVideoUrl(null);
  };

  /* const allPromptsHaveVideos = () => {
    return promptArray.every((_, index) => videoFiles.hasOwnProperty(index));
  }; */

  const canDeletePrompts = promptArray.length > minPrompts;

  const applyLocalPromptDelete = (index: number) => {
    setOpenPromptIndex(null);

    setPromptArray((prev) => prev.filter((_, i) => i !== index));

    setDescriptions((prev) => {
      const next = { ...prev };
      delete next[index];
      const re: { [key: number]: string } = {};
      Object.keys(next).forEach((k) => {
        const oldIdx = parseInt(k, 10);
        const newIdx = oldIdx > index ? oldIdx - 1 : oldIdx;
        if (next[oldIdx] !== undefined) re[newIdx] = next[oldIdx];
      });
      return re;
    });

    setVideoFiles((prev) => {
      const copy = { ...prev };
      delete copy[index];
      const re: VideoFiles = {};
      Object.keys(copy).forEach((k) => {
        const oldIdx = parseInt(k, 10);
        const newIdx = oldIdx > index ? oldIdx - 1 : oldIdx;
        if (copy[oldIdx]) re[newIdx] = copy[oldIdx];
      });
      return re;
    });

    setDocumentFiles((prev) => {
      const copy = { ...prev };
      delete copy[index];
      const re: { [key: number]: File } = {};
      Object.keys(copy).forEach((k) => {
        const oldIdx = parseInt(k, 10);
        const newIdx = oldIdx > index ? oldIdx - 1 : oldIdx;
        if (copy[oldIdx]) re[newIdx] = copy[oldIdx];
      });
      return re;
    });

    setPromptIds((prev) => {
      const copy = { ...prev };
      delete copy[index];
      const re: Record<number, number> = {};
      Object.keys(copy).forEach((k) => {
        const oldIdx = parseInt(k, 10);
        const newIdx = oldIdx > index ? oldIdx - 1 : oldIdx;
        if (copy[oldIdx] != null) re[newIdx] = copy[oldIdx];
      });
      return re;
    });

    setDismissedEnhancementCards((prev) => {
      const copy = { ...prev };
      delete copy[index];
      const re: { [key: number]: boolean } = {};

      Object.keys(copy).forEach((k) => {
        const oldIdx = parseInt(k, 10);
        const newIdx = oldIdx > index ? oldIdx - 1 : oldIdx;
        re[newIdx] = copy[oldIdx];
      });

      return re;
    });

    reindexVideoUploadState(index);
    reindexDocumentUploadState(index);

    setEditingIndex((prev) => {
      if (prev == null) return prev;
      if (prev === index) return null;
      return prev > index ? prev - 1 : prev;
    });
    if (editingIndexRef.current != null) {
      if (editingIndexRef.current === index) editingIndexRef.current = null;
      else if (editingIndexRef.current > index) editingIndexRef.current -= 1;
    }
  };

  const handleDeletePrompt = async (index: number) => {
    if (promptArray.length <= minPrompts) {
      toast.error(`You need at least ${minPrompts} prompts.`);
      return;
    }
    if (deletingPromptIndex !== null) {
      return;
    }

    setDeletingPromptIndex(index);

    try {
      const promptId = promptIds[index];
      if (promptId) {
        await axiosInstance.delete(`${API_ROUTES.PROMPTS.DELETE_PROMPT}/${promptId}`);
        await useSessionStore.getState().refreshPrompts({ force: true });
        window.dispatchEvent(new Event('prompts-updated'));
      }

      applyLocalPromptDelete(index);
      toast.success('Prompt deleted successfully');
    } catch (error) {
      console.error('Delete prompt failed:', error);
      toast.error(getUserFacingError(error, 'We could not delete that prompt. Please try again.'));
    } finally {
      setDeletingPromptIndex(null);
    }
  };

  /*   const getProgressKey = () => {
    if (Object.keys(videoFiles).length >= 4) return 'allPrompts';
    if (Object.keys(videoFiles).length >= 1) return 'firstPrompt';
    if (currentStep === 2) return 'promptsGenerated';
    return 'welcome'; // or earlier stage
  }; */

  const promptReviewSummary = useMemo(() => {
    const total = promptArray.length;
    let withVideo = 0;
    let withDescription = 0;
    let withDocument = 0;
    let fullyReady = 0;

    promptArray.forEach((_, index) => {
      const hasVideo = Boolean(videoFiles[index] || videoUrls[index]);
      const hasDescription = Boolean(descriptions[index]?.trim());
      const hasDocument = Boolean(documentFiles[index] || documentUrls[index]);
      if (hasVideo) withVideo += 1;
      if (hasDescription) withDescription += 1;
      if (hasDocument) withDocument += 1;
      if (promptRowStatus(hasVideo, hasDescription) === 'complete') {
        fullyReady += 1;
      }
    });

    return {
      total,
      withVideo,
      withDescription,
      withDocument,
      fullyReady,
      missingVideos: Math.max(0, Math.min(total, 4) - withVideo),
    };
  }, [promptArray, videoFiles, videoUrls, descriptions, documentFiles, documentUrls]);

  const roleSetupEncouragement = useMemo(
    () => getRoleSetupEncouragement(jobIndustry, jobTitle, level),
    [jobIndustry, jobTitle, level]
  );

  const profileProgressPercent = useMemo(() => {
    const session = useSessionStore.getState();
    const draftPrompts = draftPromptsForScoring(
      promptArray.map((_, index) => ({
        hasVideo: Boolean(videoFiles[index] || videoUrls[index]),
        hasDescription: Boolean(descriptions[index]?.trim()),
      }))
    );
    return resolveScreeningReadinessPercent(session.currentUser, draftPrompts, session.portfolio);
  }, [promptArray, videoFiles, videoUrls, descriptions]);

  const promptReadinessPercent = useMemo(() => {
    const total = Math.max(promptReviewSummary.total, 4);
    return Math.round((promptReviewSummary.fullyReady / total) * 100);
  }, [promptReviewSummary.fullyReady, promptReviewSummary.total]);

  return (
    <>
      {loader ? (
        <PageBusyLoader pageName="Prompts" className="page-busy-loader--create-prompt" />
      ) : (
        <Container className="form-infor-profile">
          {currentStep !== 3 && !changeRoleFlow ? <OnboardingProgress /> : null}
          {showFirstPromptMilestone && currentStep === 2 && videoCount >= 1 && videoCount < 4 && (
            <div className="prompt-milestone-card" role="status">
              <button
                type="button"
                className="prompt-milestone-close"
                onClick={() => setShowFirstPromptMilestone(false)}
                aria-label="Dismiss prompt progress card"
              >
                ×
              </button>
              <div className="prompt-milestone-badge">{profileProgressPercent}% complete</div>
              <div className="prompt-milestone-title">First prompt unlocked</div>
              <p className="prompt-milestone-copy">
                Great start. Add {remainingVideos} more video
                {remainingVideos === 1 ? '' : 's'} to complete your prompt set.
              </p>
              <div className="prompt-milestone-progress-track">
                <div
                  className="prompt-milestone-progress-fill"
                  style={{ width: `${profileProgressPercent}%` }}
                />
              </div>
            </div>
          )}
          <div className="prompt-header-container">
            {currentStep !== 3 && (
              <h2 className="title-dash flex2">
                {changeRoleFlow && currentStep === 1
                  ? 'Select Your Job Role'
                  : 'Create Screening Prompts'}
              </h2>
            )}
            {currentStep === 2 && promptArray.length < maxPrompts && (
              <Button variant="success" onClick={() => onModalOpen()}>
                Add prompt
              </Button>
            )}
          </div>
          {currentStep === 2 && (
            <div className="prompt-step-intro" role="note">
              <ul className="prompt-step-intro__list">
                <li className="prompt-step-intro__item">
                  <span className="prompt-step-intro__marker" aria-hidden>
                    1
                  </span>
                  <span>
                    <strong>4 prompts to start</strong> — 2 general plus 2 tailored to your role
                    (most recommended picks).
                  </span>
                </li>
                <li className="prompt-step-intro__item">
                  <span className="prompt-step-intro__marker" aria-hidden>
                    2
                  </span>
                  <span>
                    Add or swap via <strong>Add prompt</strong> (keep 4–6 total).
                  </span>
                </li>
                <li className="prompt-step-intro__item">
                  <span className="prompt-step-intro__marker" aria-hidden>
                    3
                  </span>
                  <span>Video, descriptions, and documents are optional until you submit.</span>
                </li>
                <li className="prompt-step-intro__item">
                  <span className="prompt-step-intro__marker" aria-hidden>
                    4
                  </span>
                  <span>
                    Click <strong>Customize</strong> on a card to change the prompt, add media, or
                    attach a document.
                  </span>
                </li>
              </ul>
            </div>
          )}
          <div
            className={`form-container${
              currentStep === 1 ? ' form-container--role-setup' : ''
            }${currentStep === 3 ? ' form-container--summary' : ''}`}
          >
            {currentStep === 1 && (
              <div className="prompt-role-setup">
                <div className="prompt-role-setup__intro">
                  <p className="prompt-role-setup__eyebrow">Screening prompts</p>
                  <h3 className="prompt-role-setup__heading">Tell us about your target role</h3>
                  <p className="prompt-role-setup__lede">
                    We&apos;ll use this to recommend screening questions that fit your industry and
                    experience.
                  </p>
                </div>
                <section className="prompt-setup-section">
                  <header className="prompt-setup-section__header">
                    <span className="prompt-setup-section__index" aria-hidden>
                      1
                    </span>
                    <div>
                      <h3 className="prompt-setup-section__title">Job industry</h3>
                      <p className="prompt-setup-section__hint">
                        Ensure the category you select aligns closely with the specific job role you
                        are applying for.
                      </p>
                    </div>
                  </header>
                  <div className="prompt-setup-section__body">
                    <Dropdown onSelect={handleSelectIndustry}>
                      <Dropdown.Toggle
                        variant="success"
                        id="dropdown-industry"
                        className="prompt-dropdown-industries w-100"
                      >
                        {jobIndustry || 'Select industry'}
                      </Dropdown.Toggle>
                      <Dropdown.Menu className="prompt-setup-dropdown-menu">
                        {industries.map((item) => (
                          <Dropdown.Item eventKey={item} key={item}>
                            {item}
                          </Dropdown.Item>
                        ))}
                      </Dropdown.Menu>
                    </Dropdown>
                    {errors.noIndustry && (
                      <Alert variant="danger" className="mt-2">
                        {PROVIDE_INDUSTRY}
                      </Alert>
                    )}
                  </div>
                </section>

                {jobIndustry && (
                  <section className="prompt-setup-section">
                    <header className="prompt-setup-section__header">
                      <span className="prompt-setup-section__index" aria-hidden>
                        2
                      </span>
                      <div>
                        <h3 className="prompt-setup-section__title">Job title</h3>
                        <p className="prompt-setup-section__hint">
                          Type the specific job role which is consistent with your current role.
                        </p>
                      </div>
                    </header>
                    <div className="prompt-setup-section__body">
                      <Form.Control
                        type="text"
                        value={jobTitle}
                        onChange={(e) => setJobTitle(e.target.value)}
                        placeholder="e.g. Senior Software Engineer"
                        required
                        className="prompt-setup-input"
                      />
                      {errors.noTitle && (
                        <Alert variant="danger" className="mt-2">
                          {PROVIDE_JOB_TITLE}
                        </Alert>
                      )}
                    </div>
                  </section>
                )}

                {jobTitle && (
                  <section className="prompt-setup-section">
                    <header className="prompt-setup-section__header">
                      <span className="prompt-setup-section__index" aria-hidden>
                        3
                      </span>
                      <div>
                        <h3 className="prompt-setup-section__title">Job level</h3>
                        <p className="prompt-setup-section__hint">
                          Select the level of the role that is consistent with your experience
                          and/or jobs you seek.
                        </p>
                      </div>
                    </header>
                    <div className="prompt-setup-section__body">
                      <div className="prompt-level-options" role="radiogroup">
                        {JOB_LEVEL_OPTIONS.map((currentLevel) => (
                          <label
                            key={currentLevel}
                            className={`prompt-level-option${
                              level === currentLevel ? ' prompt-level-option--selected' : ''
                            }`}
                            htmlFor={`${currentLevel}_level`}
                          >
                            <Form.Check
                              type="radio"
                              name="jobLevel"
                              checked={level === currentLevel}
                              onChange={() => handleChangeLevel(currentLevel)}
                              id={`${currentLevel}_level`}
                              className="level_color"
                            />
                            <span className="prompt-level-option__label">
                              {handleLevelExperience(currentLevel)}
                            </span>
                          </label>
                        ))}
                      </div>
                      {errors.noLevel && (
                        <Alert variant="danger" className="mt-2">
                          {PROVIDE_LEVEL}
                        </Alert>
                      )}
                    </div>
                  </section>
                )}
              </div>
            )}

            {currentStep === 2 && (
              <>
                {promptArray.length === 0 ? (
                  <div className="prompt-selection-empty">
                    <p>No prompts selected yet.</p>
                    <p>
                      Use <strong>Add prompt</strong> to pick your screening questions (4–6
                      required).
                    </p>
                    <Button variant="success" onClick={() => onModalOpen()}>
                      Add prompt
                    </Button>
                  </div>
                ) : null}
                {promptArray.map((item, index) => {
                  const hasDescription = Boolean(descriptions[index]?.trim());
                  const hasVideo = Boolean(videoFiles[index]);
                  const hasDocument = hasPromptDocument(index);
                  const needsEnhancement =
                    hasVideo &&
                    (!hasDescription || !hasDocument) &&
                    !dismissedEnhancementCards[index];

                  return (
                    <Row className="prompt-container-list card-style-list" key={index}>
                      <Col sm={12}>
                        <div className="prompt-card-ui">
                          <input
                            type="file"
                            id={`doc-upload-${index}`}
                            className="prompt-doc-upload-input"
                            title="Upload supporting document"
                            accept=".pdf,.doc,.docx,.txt"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                setDocumentFiles((prev) => ({
                                  ...prev,
                                  [index]: file,
                                }));
                                setDismissedEnhancementCards((prev) => ({
                                  ...prev,
                                  [index]: false,
                                }));
                                toast.success('Document uploaded successfully!');
                              }
                            }}
                          />

                          <div className="prompt-main-text">
                            <p onClick={() => togglePrompt(index)}>{item}</p>
                          </div>

                          {renderVideoUploadBar(index)}

                          <div className="prompt-card-footer">
                            {hasVideo && (
                              <Button
                                className="prompt-button view-video-btn"
                                variant="outline-primary"
                                onClick={() => viewVideo(index)}
                              >
                                <PlayIcon width={18} height={18} className="prompt-icon-button" />
                                View Video
                              </Button>
                            )}

                            {hasDocument && (
                              <Button
                                className="prompt-button view-video-btn"
                                variant="outline-success"
                                onClick={() => {
                                  const file = documentFiles[index];
                                  if (file) {
                                    const url = URL.createObjectURL(file);
                                    window.open(url, '_blank');
                                  }
                                }}
                              >
                                <UploadIcon
                                  width={18}
                                  height={18}
                                  className="prompt-icon-button green-icon"
                                  style={{ marginRight: '8px' }}
                                />
                                View Document
                              </Button>
                            )}

                            {hasDescription && (
                              <Button
                                onClick={() => handleOpenDescriptionModal(index)}
                                className="prompt-button view-video-btn"
                                variant="outline-success"
                              >
                                <DescriptionIcon
                                  width={18}
                                  height={18}
                                  className="prompt-icon-button green-icon"
                                  style={{ marginRight: '8px' }}
                                />
                                View Description
                              </Button>
                            )}

                            <Button
                              className={`prompt-button ${
                                hasVideo || hasDescription || hasDocument
                                  ? 'edit-prompt-btn'
                                  : 'customize-btn'
                              }`}
                              variant={
                                hasVideo || hasDescription || hasDocument
                                  ? 'outline-success'
                                  : 'success'
                              }
                              onClick={() => handleCustomizePrompt(index)}
                            >
                              {hasVideo || hasDescription || hasDocument ? (
                                <>
                                  <PencilSquare
                                    width={16}
                                    height={16}
                                    className="me-1"
                                    aria-hidden
                                  />
                                  Edit
                                </>
                              ) : (
                                'Customize'
                              )}
                            </Button>

                            {canDeletePrompts && (
                              <Button
                                className="prompt-button"
                                variant="outline-danger"
                                onClick={() => void handleDeletePrompt(index)}
                                disabled={deletingPromptIndex !== null}
                                style={{ marginLeft: 8 }}
                              >
                                {deletingPromptIndex === index ? (
                                  <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    aria-hidden="true"
                                  />
                                ) : (
                                  <DeleteIcon
                                    width={18}
                                    height={18}
                                    className="prompt-icon-button"
                                  />
                                )}
                                {deletingPromptIndex === index ? 'Deleting...' : 'Delete'}
                              </Button>
                            )}
                          </div>

                          {needsEnhancement && (
                            <div className="prompt-enhance-card">
                              <button
                                type="button"
                                className="prompt-enhance-close"
                                aria-label="Dismiss enhancement suggestion"
                                onClick={() =>
                                  setDismissedEnhancementCards((prev) => ({
                                    ...prev,
                                    [index]: true,
                                  }))
                                }
                              >
                                ×
                              </button>
                              <div className="prompt-enhance-header">
                                <DescriptionIcon
                                  width={18}
                                  height={18}
                                  className="prompt-enhance-icon"
                                />
                                <span>Enhance this prompt to stand out</span>
                              </div>
                              <p className="prompt-enhance-copy">
                                {hasDescription
                                  ? 'Add a supporting document to strengthen this prompt.'
                                  : hasDocument
                                    ? 'Add a brief description so employers understand your response context.'
                                    : 'Add both a short description and a supporting document for a stronger submission.'}
                              </p>
                              <div className="prompt-enhance-actions">
                                {!hasDescription && (
                                  <Button
                                    className="prompt-enhance-btn prompt-enhance-btn-primary"
                                    variant="success"
                                    onClick={() => handleOpenDescriptionModal(index)}
                                  >
                                    <PlusIcon width={16} height={16} />
                                    Add Description
                                  </Button>
                                )}
                                {!hasDocument && (
                                  <Button
                                    className="prompt-enhance-btn prompt-enhance-btn-secondary"
                                    variant="light"
                                    onClick={() =>
                                      document.getElementById(`doc-upload-${index}`)?.click()
                                    }
                                  >
                                    <UploadIcon width={16} height={16} />
                                    Attach Document
                                  </Button>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </Col>
                    </Row>
                  );
                })}

                {/* Browse prompt list action is in the header */}
              </>
            )}

            {currentStep === 3 && (
              <div className="prompt-summary-panel">
                <div className="prompt-summary-page-header">
                  <div className="prompt-summary-page-heading">
                    <h2 className="prompt-summary-page-title">Review before you submit</h2>
                    <p className="prompt-summary-page-subtitle">
                      You&apos;re almost done! Here&apos;s a summary of your role setup and prompt
                      responses.
                    </p>
                  </div>
                  <div className="prompt-summary-readiness">
                    <div className="prompt-summary-readiness__top">
                      <span className="prompt-summary-readiness__label">Prompt Health</span>
                      <span className="prompt-summary-readiness__percent">
                        {promptReadinessPercent}%
                      </span>
                    </div>
                    <div className="prompt-summary-readiness__track">
                      <div
                        className="prompt-summary-readiness__fill"
                        style={{ width: `${promptReadinessPercent}%` }}
                      />
                    </div>
                    <span className="prompt-summary-readiness__caption">
                      {promptReviewSummary.fullyReady} of {Math.max(promptReviewSummary.total, 4)}{' '}
                      prompts fully completed
                    </span>
                  </div>
                </div>

                <p className="prompt-summary-submit-note">
                  When you&apos;re ready, submit your prompts. You can continue editing videos,
                  descriptions, and supporting documents anytime from your dashboard.
                </p>

                <div className="prompt-summary-role-card">
                  <div className="prompt-summary-role-card__header">
                    <div className="prompt-summary-role-card__title-wrap">
                      <span className="prompt-summary-role-card__icon" aria-hidden>
                        <CaseIcon width={18} height={18} />
                      </span>
                      <h3 className="prompt-summary-section-title">Role Setup Summary</h3>
                    </div>
                    <button
                      type="button"
                      className="prompt-summary-edit-btn"
                      onClick={() => setCurrentStep(1)}
                    >
                      <EditIcon width={14} height={14} aria-hidden />
                      Edit
                    </button>
                  </div>
                  <dl className="prompt-summary-setup-grid prompt-summary-setup-grid--role">
                    <div>
                      <dt>Industry</dt>
                      <dd>{jobIndustry || '—'}</dd>
                    </div>
                    <div>
                      <dt>Job Title</dt>
                      <dd>{jobTitle || '—'}</dd>
                    </div>
                    <div>
                      <dt>Experience Level</dt>
                      <dd>{formatJobLevel(level) || '—'}</dd>
                    </div>
                    <div>
                      <dt>Prompts Selected</dt>
                      <dd>{promptReviewSummary.total}</dd>
                    </div>
                  </dl>
                </div>

                <div className="prompt-summary-card">
                  <div className="prompt-summary-card-header">
                    <h3 className="prompt-summary-section-title">Prompt Responses Summary</h3>
                  </div>

                  <div className="prompt-summary-table-wrap">
                    <div className="prompt-summary-table-head">
                      <span>#</span>
                      <span>PROMPT</span>
                      <span>RESPONSE SUMMARY</span>
                      <span>STATUS</span>
                      <span>ACTIONS</span>
                    </div>

                    <div className="prompt-summary-rows">
                      {promptArray.map((item, index) => {
                        const hasVideo = Boolean(videoFiles[index] || videoUrls[index]);
                        const hasDescription = Boolean(descriptions[index]?.trim());
                        const hasDocument = hasPromptDocument(index);
                        const wordCount = countWords(descriptions[index] || '');
                        const rowStatus = promptRowStatus(hasVideo, hasDescription);

                        return (
                          <div key={index} className="prompt-summary-row">
                            <span className="prompt-summary-row-index">{index + 1}</span>
                            <p className="prompt-summary-row-text">{item}</p>
                            <div className="prompt-summary-row-response">
                              {isVideoUploadActive(index) ? (
                                renderVideoUploadBar(
                                  index,
                                  'create-prompt-video-upload create-prompt-video-upload--summary'
                                )
                              ) : (
                                <PromptSummaryResponseSummary
                                  hasVideo={hasVideo}
                                  hasDocument={hasDocument}
                                  hasDescription={hasDescription}
                                  wordCount={wordCount}
                                  videoSource={videoFiles[index] ?? videoUrls[index] ?? null}
                                  onViewVideo={hasVideo ? () => viewVideo(index) : undefined}
                                  onViewDocument={
                                    hasDocument ? () => viewDocument(index) : undefined
                                  }
                                  onViewDescription={
                                    hasDescription ? () => handleViewDescription(index) : undefined
                                  }
                                />
                              )}
                            </div>
                            <div className="prompt-summary-row-status">
                              <span
                                className={`prompt-summary-status-badge prompt-summary-status-badge--${rowStatus}`}
                              >
                                {rowStatus === 'complete' ? 'Complete' : 'Partial'}
                              </span>
                            </div>
                            <div className="prompt-summary-row-actions">
                              <button
                                type="button"
                                className="prompt-summary-edit-row-btn"
                                title="Edit prompt response"
                                onClick={() => handleCustomizePrompt(index)}
                              >
                                <PencilSquare width={14} height={14} />
                                Edit
                              </button>
                              {canDeletePrompts ? (
                                <button
                                  type="button"
                                  className="prompt-summary-delete-row-btn"
                                  title="Delete prompt"
                                  onClick={() => void handleDeletePrompt(index)}
                                  disabled={deletingPromptIndex !== null}
                                >
                                  {deletingPromptIndex === index ? (
                                    <Spinner
                                      as="span"
                                      animation="border"
                                      size="sm"
                                      role="status"
                                      aria-hidden="true"
                                    />
                                  ) : (
                                    <DeleteIcon width={14} height={14} />
                                  )}
                                  {deletingPromptIndex === index ? 'Deleting...' : 'Delete'}
                                </button>
                              ) : null}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <div className="prompt-summary-footer">
                  <Button
                    type="button"
                    className="prompt-summary-back-btn"
                    onClick={() => setCurrentStep(2)}
                  >
                    <ArrowLeft aria-hidden />
                    Back
                  </Button>
                  <div className="prompt-summary-footer-actions">
                    <Button
                      type="button"
                      className="prompt-summary-save-later-btn"
                      onClick={handleSave}
                      disabled={isSaving || isSubmitting || loader || uploadsPending}
                    >
                      {isSaving ? (
                        <>
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                            className="me-2"
                          />
                          Saving...
                        </>
                      ) : (
                        'Save & Continue Later'
                      )}
                    </Button>
                    <Button
                      type="button"
                      onClick={onSubmit}
                      className="prompt-summary-submit-btn"
                      disabled={isSaving || isSubmitting || loader || uploadsPending}
                    >
                      {isSubmitting ? (
                        <>
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                            className="me-2"
                          />
                          Submitting...
                        </>
                      ) : (
                        'Submit Prompts'
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {currentStep !== 3 && (
              <div
                className={`d-flex justify-content-between mt-4${
                  currentStep === 1 ? ' prompt-role-setup-actions' : ''
                }`}
              >
                {currentStep === 1 && (
                  <div className="prompt-role-setup-footer">
                    <div className="prompt-encouragement prompt-encouragement--inline">
                      <div className="prompt-encouragement-art" aria-hidden="true">
                        <img
                          src={thumbsUpEncouragement}
                          alt=""
                          className="prompt-encouragement-mascot"
                        />
                      </div>
                      <div className="prompt-encouragement-copy">{roleSetupEncouragement}</div>
                    </div>
                    <Button
                      variant="success"
                      className="prompt-role-setup-next"
                      onClick={handleNextStep}
                    >
                      Next
                    </Button>
                  </div>
                )}
                {currentStep === 2 && (
                  <>
                    <Button
                      variant="success"
                      onClick={handlePrevStep}
                      style={{ padding: '6px 16px' }}
                    >
                      Previous
                    </Button>
                    <div
                      style={{
                        display: 'flex',
                        gap: '10px',
                        justifyContent: 'flex-end',
                      }}
                    >
                      {/* Save Button */}
                      <Button
                        variant="warning"
                        onClick={handleSave}
                        disabled={isSaving || uploadsPending}
                      >
                        {isSaving ? (
                          <>
                            <Spinner
                              as="span"
                              animation="border"
                              size="sm"
                              role="status"
                              aria-hidden="true"
                              className="me-2"
                            />
                            Saving...
                          </>
                        ) : (
                          'Save'
                        )}
                      </Button>

                      {/* Next Button */}
                      <div className="tooltip-container">
                        <Button variant="success" onClick={handleNextStep}>
                          Next
                        </Button>
                        {/*  {!allPromptsHaveVideos() && (
                        <span className="tooltip-text">
                          Upload videos for all prompts
                        </span>
                      )} */}
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </Container>
      )}
      <Modal
        show={showModal && !recordOrUploadModal}
        onHide={onModalClose}
        centered
        size="lg"
        dialogClassName="prompt-picker-modal"
        scrollable
      >
        <PromptSelectionModal
          jobIndustry={jobIndustry}
          jobTitle={jobTitle}
          level={level}
          roleSpecificPrompts={roleSpecificPromptPool}
          onModalClose={() => setShowModal(false)}
          onPromptSelect={handlePromptSelect}
          onEnsurePromptInSet={ensurePromptInSet}
          videoFiles={videoFiles}
          setVideoFiles={setVideoFiles}
          descriptions={descriptions}
          setDescriptions={setDescriptions}
          editingIndex={editingIndex}
          setEditingIndex={setEditingIndex}
          promptArray={promptArray}
          onClearVideoAtIndex={clearVideoUploadState}
        />
      </Modal>
      <Modal show={showPortfolioModal} centered>
        <Modal.Body className="modern-modal-body">
          <p className="modal-text">You have successfully created your screening Prompts!</p>
          <div className="prompt-modal-container-portfolio">
            <Button variant="secondary" className="modern-btn" onClick={navigateToUserProfile}>
              OK
            </Button>
          </div>
          {/* {!portfolioData && (
            <>
              <p className="portfolio-explanation">
                Adding a portfolio allows you to showcase your best work and
                skills to potential employers or clients. It increases your
                chances of standing out and getting noticed.
              </p>
              <div className="prompt-modal-container-portfolio">
                <Button
                  variant="success"
                  className="modern-btn recommended-button"
                  onClick={() => {
                    setModalStep(2);
                    navigate('/create-portfolio');
                  }}
                >
                  Add Portfolio
                  <span className="recommended">⭐</span>
                </Button>

                <Button
                  variant="secondary"
                  className="modern-btn"
                  onClick={navigateToUserProfile}
                >
                  Not Now
                </Button>
              </div>
            </>
          )*/}
          {/*   {!portfolioData && (
            <div className="prompt-modal-container-portfolio">
              <Button
                variant="success"
                className="modern-btn"
                onClick={navigateToUserProfile}
              >
                Done
              </Button>
            </div>
          )} */}
        </Modal.Body>
      </Modal>
      {openPromptIndex !== null && !recordOrUploadModal && !showModal && (
        <>
          <input
            type="file"
            id={`doc-upload-${openPromptIndex}`}
            className="prompt-doc-upload-input"
            title="Upload supporting document"
            accept=".pdf,.doc,.docx,.txt"
            onChange={(e) => {
              const file = e.target.files?.[0];
              const index = openPromptIndex;
              if (file && index !== null) {
                setDocumentFiles((prev) => ({
                  ...prev,
                  [index]: file,
                }));
                setDismissedEnhancementCards((prev) => ({
                  ...prev,
                  [index]: false,
                }));
                toast.success('Document uploaded successfully!');
              }
            }}
          />
          {renderPromptManageModal(openPromptIndex)}
        </>
      )}
      <Modal
        show={recordOrUploadModal}
        onHide={handleModalClose}
        centered
        className="recording-studio-modal"
        backdropClassName="recording-studio-modal-backdrop"
      >
        <RecordOrUploadModal
          videoFiles={videoFiles}
          setVideoFiles={setVideoFiles}
          documentFiles={documentFiles}
          setDocumentFiles={setDocumentFiles}
          openPromptIndex={openPromptIndex}
          editingIndex={editingIndex}
          recordOrUploadModal={recordOrUploadModal}
          onModalClose={handleModalClose}
          errors={errors}
          setEditingIndex={setEditingIndex}
          setErrors={setErrors}
          hideSubmit
          preferRecordingStudio
          promptText={
            (editingIndex ?? openPromptIndex) != null
              ? promptArray[editingIndex ?? openPromptIndex ?? 0]
              : undefined
          }
          onClearVideoAtIndex={clearVideoUploadState}
          uploadProgressSlot={
            (openPromptIndex ?? editingIndex) != null &&
            isVideoUploadActive(openPromptIndex ?? editingIndex ?? -1)
              ? renderVideoUploadBar(
                  openPromptIndex ?? editingIndex ?? 0,
                  'create-prompt-video-upload create-prompt-video-upload--modal'
                )
              : null
          }
        />
      </Modal>
      <Modal show={showVideoPlayer} onHide={closeVideoPlayer} centered size="lg">
        <Modal.Body>
          {selectedVideoUrl && (
            <video controls autoPlay src={selectedVideoUrl} style={{ width: '100%' }}>
              Your browser does not support the video tag.
            </video>
          )}
        </Modal.Body>
      </Modal>
      <Modal show={isDescriptionModalOpen} onHide={handleCancelDescription} size="lg" centered>
        <PromptDescriptionModal
          onModalClose={handleCancelDescription}
          setDescriptions={setDescriptions}
          currentDescription={currentDescription}
          setCurrentDescription={setCurrentDescription}
          editingIndex={editingIndex}
          mode={descriptionModalMode}
        />
      </Modal>
      {showWholePromptRemovalModal && (
        <Modal
          show={showWholePromptRemovalModal}
          onHide={() => setWholePromptRemovalModal(false)}
          centered
        >
          <Modal.Header closeButton />
          <Modal.Body style={{ overflowY: 'auto' }} className="prompt-modal-container p-5">
            <div>
              <Row sm={12}>
                <Modal.Title className="mb-3">
                  <h4>Are you sure you want to delete all prompts for {jobTitle}?</h4>
                </Modal.Title>
                <Col style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Button
                    variant="success"
                    className="modal-sub-title"
                    onClick={handleDeleteWholePrompt}
                    style={{ marginRight: '10px' }}
                  >
                    Delete all
                  </Button>
                  <Button
                    variant="secondary"
                    className="modal-sub-title"
                    onClick={() => setWholePromptRemovalModal(false)}
                  >
                    Cancel
                  </Button>
                </Col>
              </Row>
            </div>
          </Modal.Body>
        </Modal>
      )}
      {/* ✅ For the 70% first prompt achievement */}
      <Modal
        show={showFinalizeModal}
        onHide={isSubmitting ? undefined : closeFinalizeModal}
        backdrop={isSubmitting ? 'static' : true}
        keyboard={!isSubmitting}
        centered
        className="finalize-prompts-modal"
      >
        <Modal.Body className="finalize-prompts-modal__body text-center">
          <div className="finalize-prompts-modal__icon" aria-hidden>
            <CheckCircleFill size={36} />
          </div>
          <Modal.Title className="finalize-prompts-modal__title">
            Finalize Screening prompts
          </Modal.Title>
          <p className="finalize-prompts-modal__copy">
            Are you ready to confirm and submit your screening prompts?
          </p>
          <Form.Check
            id="profile-storage-consent"
            className="finalize-prompts-modal__consent"
            type="checkbox"
            checked={profileConsentAccepted}
            onChange={(e) => setProfileConsentAccepted(e.target.checked)}
            label={PROFILE_STORAGE_CONSENT_TEXT}
          />
        </Modal.Body>
        <Modal.Footer className="finalize-prompts-modal__footer">
          <Button
            variant="light"
            className="finalize-prompts-modal__btn-secondary"
            onClick={closeFinalizeModal}
            disabled={isSubmitting}
          >
            Stay & make changes
          </Button>
          <Button
            variant="success"
            className="finalize-prompts-modal__btn-primary"
            onClick={handleConfirmFinalize}
            disabled={isSubmitting || !profileConsentAccepted}
          >
            {isSubmitting ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Confirming...
              </>
            ) : (
              'Confirm & create Screening prompt'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <CongratsModal
        show={showCongrats}
        onClose={handleFirstPromptCongratsClose}
        eyebrowText="First prompt complete"
        headerText="Well done!"
        bodyText="You’ve just completed your first screening prompt. This is a big step toward building a profile that skips redundant screening calls."
        buttonText="Keep going"
      />

      <RecommendationModal
        show={showRecommendation}
        onClose={() => {
          setShowRecommendation(false);
        }}
      />
      <Modal show={showInfoPopup} onHide={() => setShowInfoPopup(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Customize Your Prompt</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Have a different question in mind? Choose between <b>General</b> or{' '}
          <b>Role-Specific Prompts</b>
          to better showcase your skills.
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="success"
            onClick={() => {
              // ✅ 1. Close this info popup
              setShowInfoPopup(false);

              // ✅ 2. Immediately open the real customize modal
              setShowModal(true);
            }}
          >
            Got it
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default CreatePrompt;
